import React, { useState, useEffect } from 'react';
import ReportsHeader from '../ReportsHeader';
import OperatorItem from './OperatorItem';
import { AppDatePicker, Tooltip } from 'components';
import { getNormalizedUrl } from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSortedOperatorsListStart } from '../../../redux/operators/operators.actions';
import { getCalenderLastDays } from '../../../utils/date';
import ApiLoader from '../../ApiLoader';
import { FileIcon } from 'assets/icons';
import moment from 'jalali-moment';
import { CSVLink } from 'react-csv';
import AccessOverlay from 'components/AccessOverlay';
import { getTimeFromSeconds } from 'utils/date';
import useTranslation from 'hooks/useTranslation';
import { useMediaQuery } from 'react-responsive';

import './styles.scss';
import DashboardMainSectionWrapper from 'layouts/Dashboard/DashboardMainSectionWrapper';

const mapState = (state) => ({
  operators: state.operatorsData.operators,
  profile: state.userData.profile,
  operatorsTableData: state.operatorsData.operatorsTableData,
  sortedOperators: state.operatorsData.sortedOperators,
});

const getCellValue = (value) => {
  if (value !== undefined && value !== null) return value;
  return '-';
};

const lastWeekDate = getCalenderLastDays(6, 'days');

const ALLOWED_TYPES = [2, 3, 7, 8, 10, 11]; // آزمایشی + تجاری + پریمیوم + پریمیوم پلاس + وی آی پی

const ALLOWED_TYPES_TO_EXPORT_EXCEL = [3, 8, 10, 11]; // premium + experimental + پریمیوم پلاس

const OperatorsReport = (props) => {
  const [selectedDate, setSelectedDate] = useState(lastWeekDate);
  const { profile, sortedOperators } = useSelector(mapState);
  const dispatch = useDispatch();

  const hasAccessToExcelExportFeat = ALLOWED_TYPES_TO_EXPORT_EXCEL.includes(
    profile?.accountType
  );

  const datePickerWrapper = document.getElementsByClassName('rmdp-wrapper');

  const { getTranslation } = useTranslation('STATISTICS');
  const { t } = getTranslation();

  const IsScreenSmallSize = useMediaQuery({
    query: '(max-width: 992px)',
  });

  const smallSize = useMediaQuery({
    query: '(max-width: 560px)',
  });

  const getCsvExportFileName = (startDate, endDate) => {
    return `${startDate}-${endDate}-${t(
      'OPERATORS_STATISTICS_CSV_EXPORT_FILE_PREFIX'
    )}.csv`;
  };

  const getStandardizedMetricObj = (obj) => ({
    ...obj,
    name: t('OPERATORS_STATISTICS_AVERAGE_ITEM_LABEL'),
    closeAvg: obj?.closeAvgTime,
    ratePercent: obj?.rateAvg,
    startAvg: obj?.startAvgTime,
  });

  const getConvertedRawData = (dataObj) => {
    if (!dataObj) return;

    const newObj = {
      fullName: dataObj.name,

      totalWorkTime:
        dataObj.totalTime !== undefined
          ? getTimeFromSeconds(dataObj.totalTime)
          : getCellValue(dataObj.totalTime),

      averageTime:
        dataObj.avgActivity !== undefined
          ? getTimeFromSeconds(dataObj.avgActivity)
          : getCellValue(dataObj.avgActivity),

      accepted: getCellValue(dataObj.totalAccepted),

      responseTimeAverage: getTimeFromSeconds(dataObj.startAvg),

      conversationTimeAverage: getTimeFromSeconds(dataObj.closeAvg),

      csPercent: getCellValue(dataObj.ratePercent),

      score: `${getCellValue(dataObj.score)} ${
        !!dataObj.score ? t('SCORE_ITEM_PREFIX') : ''
      }`,
    };
    return newObj;
  };

  const getDataForExcelExport = (sortedOperators) => {
    const averageData = getConvertedRawData(
      getStandardizedMetricObj(sortedOperators?.metrics)
    );

    return [
      averageData,
      ...sortedOperators.data.map((item) => getConvertedRawData(item)),
    ];
  };

  const HEADER_ITEMS = [
    {
      label: t('OPERATORS_STATISTICS_TABLE_OPERATOR_COL_LABEL'),
      key: 'fullName',
    },
    {
      label: t('OPERATORS_STATISTICS_TABLE_TOTAL_ACTIVE_COL_LABEL'),
      key: 'totalWorkTime',
    },
    {
      label: t('OPERATORS_STATISTICS_TABLE_AVG_ACTIVE_COL_LABEL'),
      key: 'averageTime',
    },
    {
      label: t('OPERATORS_STATISTICS_TABLE_ACCEPTED_CONVS_LABEL'),
      key: 'accepted',
    },
    {
      label: t('OPERATORS_STATISTICS_TABLE_AVG_ANS_LABEL'),
      key: 'responseTimeAverage',
    },
    {
      label: t('OPERATORS_STATISTICS_TABLE_AVG_CLOSE_LABEL'),
      key: 'conversationTimeAverage',
    },
    {
      label: t('OPERATORS_STATISTICS_TABLE_CUSTOMER_SATISFACTION_LABEL'),
      key: 'csPercent',
    },
    { label: t('OPERATORS_STATISTICS_TABLE_SCORE_LABEL'), key: 'score' },
    // { label: t('OPERATORS_STATISTICS_TABLE_DETAILS_LABEL'), key: 'details' },
  ];

  const hasAccessToFeature = ALLOWED_TYPES.includes(+profile?.accountType);

  const showList =
    sortedOperators &&
    !sortedOperators.loading &&
    sortedOperators.data?.length > 0;

  const showLoading = sortedOperators && sortedOperators.loading;

  const isListEmpty =
    sortedOperators &&
    !sortedOperators.loading &&
    !sortedOperators.data?.length;

  // const getSelectedStatistic = (statistic) => {
  //   let newStatistic = [];
  //   let startDate = new Date(convertDateToEnglish(selectedDate[0], '/'))
  //     .setHours(0, 0, 0, 0)
  //     .valueOf();
  //   let endDate = new Date(convertDateToEnglish(selectedDate[1], '/'))
  //     .setHours(0, 0, 0, 0)
  //     .valueOf();

  //   if (Array.isArray(statistic) && statistic.length > 0) {
  //     statistic.forEach((item) => {
  //       if (item) {
  //         let date = new Date(item.day).setHours(0, 0, 0, 0).valueOf();

  //         if (!endDate && startDate && date === startDate) {
  //           newStatistic.push(item);
  //         }
  //         if (startDate && endDate && date <= endDate && date >= startDate) {
  //           newStatistic.push(item);
  //         }
  //       }
  //     });
  //   }

  //   return newStatistic;
  // };

  const getAvatar = (avatar) => {
    const defaultAvatar = '/images/avatar-agent-default.svg';
    return getNormalizedUrl(avatar || defaultAvatar);
  };

  const getSelectedStatistic = () => {
    const startDate = moment(new Date(selectedDate[0])).format(`YYYY-MM-DD`);
    const endDate = selectedDate[1]
      ? moment(new Date(selectedDate[1])).format(`YYYY-MM-DD`)
      : startDate;

    dispatch(
      fetchSortedOperatorsListStart({
        start_date: startDate,
        end_date: endDate,
      })
    );
  };

  useEffect(() => {
    if (smallSize && datePickerWrapper.length > 0) {
      datePickerWrapper[0].style.right = '2rem';
    }
  });

  const renderListIsEmpty = (
    <div className='non_content_wrapper'>
      {t('OPERATORS_STATISTICS_NO_OPERATOR_TO_DISPLAY')}
    </div>
  );

  const renderLoader = (
    <div className='non_content_wrapper'>
      <ApiLoader />
    </div>
  );

  const renderExcelExportBtn = () => {
    const tooltipAdditionalStyle = { width: '170px' };

    const btnContent = (
      <>
        <img src={FileIcon} alt='csv' />
        <span className='operators-csv-export-option__text'>
          {t('OPERATORS_STATISTICS_CSV_EXPORT_BTN_LABEL')}
        </span>
      </>
    );

    if (hasAccessToExcelExportFeat) {
      return (
        <div>
          <CSVLink
            className={`operators-csv-export-option ${
              showLoading ? 'disabled' : ''
            }`}
            data={getDataForExcelExport(sortedOperators)}
            headers={HEADER_ITEMS}
            onClick={(event, done) => {
              if (showLoading) return false;
              if (done) {
                return done();
              }
            }}
            filename={getCsvExportFileName(selectedDate[0], selectedDate[1])}
          >
            {btnContent}
          </CSVLink>
        </div>
      );
    }

    return (
      <Tooltip
        message={t('CSV_EXPORT_ACCESS_MSG')}
        additionalStyle={tooltipAdditionalStyle}
        placement='bottom-end'
        offsetValue={8}
      >
        <div className='operators-csv-export-option disabled'>{btnContent}</div>
      </Tooltip>
    );
  };

  return (
    <>
      <AccessOverlay allowedTypes={ALLOWED_TYPES} />
      <ReportsHeader
        title={t('OPERATORS_STATISTICS_HEADER_TITLE')}
        description={t('OPERATORS_STATISTICS_HEADER_SUB_TITLE')}
      />
      <DashboardMainSectionWrapper>
        <main className='operators-report'>
          <div className='title-container'>
            <h1 className='h1'>{t('OPERATORS_STATISTICS_MAIN_TITLE')}</h1>
            <div className='operators__report--datepickerwrapper'>
              {hasAccessToFeature && (
                <>
                  <AppDatePicker
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    placeholder={t('STATISTICS_DATE_PICKER_PLACEHOLDER')}
                    isFreePackage={profile.accountType === 0 ? true : false}
                    onClose={getSelectedStatistic}
                    position={IsScreenSmallSize ? 'bottom' : 'right'}
                  />
                  {renderExcelExportBtn()}
                </>
              )}
            </div>
          </div>

          <div className='oerators-box'>
            <table>
              <tr className='row title'>
                {HEADER_ITEMS.map((item) => (
                  <th key={item.key}>{item.label}</th>
                ))}
              </tr>

              {hasAccessToFeature && showList && (
                <>
                  <OperatorItem
                    fullName={t('OPERATORS_STATISTICS_AVERAGE_ITEM_LABEL')}
                    {...getConvertedRawData(
                      getStandardizedMetricObj(sortedOperators?.metrics)
                    )}
                  />

                  {profile?.accountType !== 0 &&
                    sortedOperators.data?.length &&
                    sortedOperators.data.map((op) => {
                      return (
                        <OperatorItem
                          id={op?._id}
                          imageUrl={getAvatar(op?.avatar)}
                          {...getConvertedRawData(op)}
                          key={op?._id}
                        />
                      );
                    })}
                </>
              )}
            </table>

            {hasAccessToFeature &&
              (isListEmpty ? renderListIsEmpty : showLoading && renderLoader)}
          </div>
        </main>
      </DashboardMainSectionWrapper>
    </>
  );
};

export default OperatorsReport;
